import { CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Form, Field } from 'react-final-form';
import * as yup from 'yup';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { getTextWithoutTags } from '../../utils/functions';
import { yupValidate } from '../../utils/yup';
import { Button, FormGroup, FormButtons, BeforeUnload } from '../common';
import { FormSelect, TextFieldWysiwyg } from './wrappers';
import { lengthField } from '../../utils/form';
import { length } from '../../utils/wysiwyg';

export interface Values {
  type: 'Mentor' | 'Founder';
  body: string;
}

interface ConsentFormProps {
  title: string;
  loading?: boolean;
  consent?: {
    type: 'Mentor' | 'Founder';
    body: string;
  };
  fixedType?: 'Mentor' | 'Founder';
  onSubmit: (parsedValues: Values) => any;
  onCancel: () => any;
}

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
  },
  title: {
    marginBottom: 32,
  },
  field: {
    '& [name="contents"]': {
      minHeight: 107,
    },
  },
  attachBtn: {
    marginTop: 24,
  },
  attachmentsList: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '20px 0 -16px -16px',
  },
  attachment: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0 0 16px 16px',

    [theme.breakpoints.up(800)]: {
      width: '50%',
    },
  },
  formButtons: {
    justifyContent: 'flex-start',
  },
}));

const createValidationSchema = () =>
  yup.object().shape({
    body: yup
      .string()
      .required('Body is required.')
      .test('not-empty-after-cleanup', 'Body is required.', function (value) {
        if (!value) return false;
        const cleanText = getTextWithoutTags(value);
        const trimmedText = cleanText.replace(/&nbsp;/g, ' ').trim();
        return trimmedText.length > 0;
      })
      .test(
        'max-length',
        `Body should not exceed ${lengthField.wysiwygDisplayLimit} characters.`,
        function (value) {
          if (!value) return true;
          return length(value) <= lengthField.wysiwygDisplayLimit;
        },
      )
      .max(
        lengthField.wysiwygMaxLength,
        `Body HTML should not exceed ${lengthField.wysiwygMaxLength} characters.`,
      ),
    type: yup.string().oneOf(['Mentor', 'Founder']).required(),
  });

function ConsentForm({
  title,
  consent,
  loading = false,
  fixedType,
  onSubmit,
  onCancel,
}: ConsentFormProps) {
  const classes = useStyles();
  const { rb } = useResourceBundles();

  const TYPES = [
    {
      label: 'Founder',
      value: 'Founder',
    },
    {
      label: rb('mentor-u'),
      value: 'Mentor',
    },
  ];

  const validationSchema = createValidationSchema();

  const initialValues: Values = {
    body: consent?.body || '',
    type: fixedType || consent?.type || (TYPES[0].value as 'Founder'),
  };

  const handleSubmit = (values: Values) => {
    onSubmit({
      ...values,
      type: fixedType || values.type,
    });
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <>
      {title && (
        <Typography className={classes.title} variant='h4'>
          {title}
        </Typography>
      )}
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validate={yupValidate(validationSchema)}
        validateOnBlur
        keepDirtyOnReinitialize
        render={(formProps) => (
          <form
            className={classes.form}
            onSubmit={formProps.handleSubmit}
            noValidate>
            <BeforeUnload
              when={formProps.dirty && !loading}
              title='Leave the page'
              body='You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
              disabled={loading}
              confirmButtonRenderer={({ onConfirm }) => (
                <Button
                  variant='outlined'
                  onClick={async () => {
                    await formProps.handleSubmit();
                    onConfirm();
                  }}
                  disabled={loading || !formProps.valid}>
                  {loading ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Save the changes'
                  )}
                </Button>
              )}
            />
            {!fixedType && (
              <FormGroup>
                <Field<string>
                  className={classes.field}
                  name='type'
                  component={FormSelect}
                  label='Type'
                  options={TYPES}
                  testid='form-consent-type'
                />
              </FormGroup>
            )}
            <FormGroup>
              <Field<string>
                className={classes.field}
                name='body'
                component={TextFieldWysiwyg}
                placeholder='Body'
                label='Body*'
                multiline
                testid='form-consent-text'
                showCount
                maxLength={lengthField.wysiwygDisplayLimit}
              />
            </FormGroup>
            <FormButtons className={classes.formButtons}>
              <Button
                data-testid='form-consent-submit'
                type='submit'
                disabled={loading}>
                {loading ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Save'
                )}
              </Button>
              <Button variant='outlined' onClick={handleCancel}>
                Cancel
              </Button>
            </FormButtons>
          </form>
        )}
      />
    </>
  );
}

export default ConsentForm;
